import { normaliseSectionKey as key, normaliseSteelSectionText, type DimensionPricing } from "./cost-recipe";
import type { QuantityDerivation } from "./quantity-dimensions";

const labelKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

function sectionContext(data?: QuantityDerivation | null, text?: string) {
  // Prefer this member's explicit section evidence to neighbouring members.
  const weights = data?.dimensions.filter(d => d.unit === "kg/m") ?? [];
  const note = weights.length === 1 ? weights[0].note ?? "" : "";
  if (/\d{2,4}\s*[x×*]\s*\d{2,4}\s*[x×*]\s*\d+(?:\.\d+)?\s*(?:UB|UC)\b/i.test(note)) {
    return normaliseSteelSectionText(note);
  }
  return normaliseSteelSectionText([text ?? "", ...(data?.dimensions ?? []).map(d => d.note ?? "")].join("\n"));
}

/** Same item evidence powers both the Dimensions display and supply resolution. */
export function steelSectionsFromEvidence(data?: QuantityDerivation | null, text?: string): string[] {
  const context = sectionContext(data, text);
  const sections = new Set<string>();
  const pattern = /\b(?:(UB|UC)\s*)?(\d{2,4})\s*[x×*]\s*(\d{2,4})\s*[x×*]\s*(\d+(?:\.\d+)?)\s*(?:kg(?:\/m)?\s*)?(UB|UC)?\b/gi;
  for (const m of context.matchAll(pattern)) {
    for (const designation of [m[1], m[5]].filter(Boolean))
      sections.add(key(`${m[2]}x${m[3]}x${m[4]}${designation}`));
  }
  return [...sections];
}

/**
 * Steel supply works like any other measured material: section + length from
 * Dimensions. An approved exact-section £/m is used when one exists; otherwise
 * the library's own reference £/kg × (length × kg/m). Never borrows another line.
 */
export function resolveSectionSupply(model: DimensionPricing, data?: QuantityDerivation | null, text?: string) {
  const rates = model.section_rates!;
  const context = sectionContext(data, text);
  for (const m of context.matchAll(/\b(UB|UC)\s*\d{2,4}\s*[x×*]\s*\d{2,4}\s*[x×*]\s*\d+(?:\.\d+)?\s*(?:kg(?:\/m)?\s*)?(UB|UC)\b/gi))
    if (m[1].toUpperCase() !== m[2].toUpperCase())
      return { error: "Conflicting UB/UC designations on this item." };
  const sections = steelSectionsFromEvidence(data, text);
  const selected = model.selected_section ? key(model.selected_section)
    : sections.length === 1 ? sections[0]
    : text === undefined && model.resolved_section ? key(model.resolved_section) : undefined;
  const dims = data?.dimensions ?? [];
  const wanted = labelKey(model.length_label ?? "Beam length");
  const metres = dims.filter(d => d.unit === "m");
  const exact = metres.filter(d => { const l = labelKey(d.label); return l === wanted || l.endsWith(` ${wanted}`); });
  const lengthDims = exact.length ? exact : metres.filter(d => /\b(length|span)$/.test(labelKey(d.label)));
  const length = model.actual_quantity ?? (lengthDims.length === 1 ? lengthDims[0].value : undefined);
  if (sections.length > 1 && !model.selected_section) return { error: "More than one steel section on this item." };
  if (length == null || !Number.isFinite(length) || length <= 0) return { error: "Add the beam length in Dimensions." };
  const rate = selected ? rates.find(r => key(r.section) === selected) : undefined;
  const round = (n: number) => Math.round(n * 100) / 100;
  const weights = dims.filter(d => d.unit === "kg/m");
  const fromDesignation = selected?.match(/X(\d+(?:\.\d+)?)(UB|UC)$/)?.[1];
  const changedWeight = weights.length === 1 && fromDesignation
    && Math.abs(weights[0].value - Number(fromDesignation)) > 0.001;
  if (rate && !changedWeight) return { section: rate.section, length, quantity: length, price: round(length * rate.rate_per_metre),
    working: `${length} m` };
  const perMetre = weights.length === 1 ? weights[0].value : fromDesignation ? Number(fromDesignation) : undefined;
  if (!perMetre || model.measure !== "weight" || !(model.reference_price ?? 0)) return { error: "Add the section weight in Dimensions." };
  const kg = Math.round(length * perMetre * 10000) / 10000;
  return { section: selected ?? "", length, quantity: kg,
    price: round(kg * model.reference_price! / model.reference_quantity),
    working: `${length} m × ${perMetre} kg/m = ${Math.round(kg * 100) / 100} kg` };
}
